import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { type DatasetImage } from "../api";
import t from "../translations";
import { useDatasetDetail } from "./DatasetDetailContext";

// The set of filters applied to the dataset image list. Every property is
// optional: a property left undefined means that dimension is not filtered,
// and an image is shown only when it satisfies every *defined* property.
//
// To add a new filter dimension (e.g. by object type):
//   1. add an optional property here,
//   2. add its query-string key to FILTER_PARAMS,
//   3. add a line each to parseFilters, writeFilters and imageMatchesFilters.
export interface DatasetImageFilters {
  // Train/test split: true = train only, false = test only, undefined = both.
  train?: boolean;
}

// Query-string key backing each filter dimension.
const FILTER_PARAMS: Record<keyof DatasetImageFilters, string> = {
  train: "fSplit",
};

// The `train` filter <-> its boolean query-string value.
function parseTrain(value: string | null): boolean | undefined {
  return value === null ? undefined : value === "true";
}

function parseFilters(params: URLSearchParams): DatasetImageFilters {
  return {
    train: parseTrain(params.get(FILTER_PARAMS.train)),
  };
}

function writeFilters(
  params: URLSearchParams,
  filters: DatasetImageFilters,
): void {
  if (filters.train === undefined) {
    params.delete(FILTER_PARAMS.train);
  } else {
    params.set(FILTER_PARAMS.train, String(filters.train));
  }
}

// An image passes only if it satisfies every filter that is actually set.
function imageMatchesFilters(
  image: DatasetImage,
  filters: DatasetImageFilters,
): boolean {
  if (filters.train !== undefined && image.train !== filters.train) {
    return false;
  }
  return true;
}

function isFilterActive(filters: DatasetImageFilters): boolean {
  return Object.values(filters).some((value) => value !== undefined);
}

// Owns the dataset image list's filtering: it reads the images loaded by the
// dataset-detail context and narrows them to the active filter set, which is
// kept in the URL query string so the filtered view is shareable.
export function useDatasetImageFilter() {
  const { images, selectedImage } = useDatasetDetail();

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const filterActive = isFilterActive(filters);

  // Everything downstream — grouping, counts, keyboard navigation — works off
  // this, so a hidden image is never grouped, counted, or selected.
  const filteredImages = useMemo(
    () =>
      filterActive
        ? images.filter((img) => imageMatchesFilters(img, filters))
        : images,
    [images, filters, filterActive],
  );

  // Merges a partial change into the active filters and persists the result to
  // the URL. Pass a property as `undefined` to clear that dimension.
  const updateFilters = (changes: Partial<DatasetImageFilters>) => {
    const next: DatasetImageFilters = { ...filters, ...changes };
    const updated = new URLSearchParams(searchParams);
    writeFilters(updated, next);
    if (updated.toString() === searchParams.toString()) return;
    setSearchParams(updated, { replace: true });
    // Warn when the new filter set hides the image currently being viewed —
    // its row drops out of the list but the panel keeps showing it.
    if (selectedImage && !imageMatchesFilters(selectedImage, next)) {
      toast.info(t.ActiveImageHiddenByFilter);
    }
  };

  return {
    filters,
    filteredImages,
    filterActive,
    updateFilters,
    // The current query string, carried onto in-view navigation so filters
    // survive switching between images.
    search: searchParams.toString(),
  };
}
